import { useState } from 'react';

import { Button, Icon, Text, Textarea, TwFileUpload } from '@ui-component-library';
import type { FileItem } from '@ui-component-library/components/FileUpload/FileUpload.types';
import {
    addSectionNote,
    addSectionNoteAttachment,
    deleteSectionNote,
    deleteSectionNoteAttachment,
    SECTION_NOTE_ACCEPT,
    SECTION_NOTE_MAX_BYTES,
    updateSectionNote,
} from '@services/sections';

import type { Property, Section } from './types';
import type { SectionNote, SectionNoteAttachment } from '../../../../../types/property';

type NotesTabProps = {
    section: Section;
    prop: Property;
    onUpdated: (ref: string, newData: unknown) => void;
};

const filesFromItems = (items: FileItem[]) =>
    items.map((item) => item.file).filter((file): file is File => file instanceof File);

const validateUploadFiles = (files: File[]): string | null => {
    const allowed = new Set(
        SECTION_NOTE_ACCEPT.split(',').map((ext) => ext.trim().replace(/^\./, '').toLowerCase()),
    );
    for (const file of files) {
        if (file.size > SECTION_NOTE_MAX_BYTES) {
            return `${file.name}: Filen får vara högst 100 MB`;
        }
        const ext = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';
        if (!ext || !allowed.has(ext)) {
            return `${file.name}: Filtypen stöds inte`;
        }
    }
    return null;
};

type NoteAttachmentsProps = {
    attachments: SectionNoteAttachment[];
    canModify: boolean;
    loading: boolean;
    onDelete: (attachmentId: number) => void;
};

function NoteAttachments({ attachments, canModify, loading, onDelete }: NoteAttachmentsProps) {
    if (!attachments.length) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            {attachments.map((attachment) => (
                <div key={attachment.id} className="flex flex-row items-start gap-2">
                    <div className="min-w-0 flex-1">
                        {attachment.is_image && attachment.url ? (
                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={attachment.url}
                                    alt={attachment.name}
                                    className="max-h-48 max-w-full rounded-md border border-solid border-[#E6E6E6]"
                                />
                            </a>
                        ) : attachment.url ? (
                            <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#2563EB] underline"
                            >
                                {attachment.name}
                            </a>
                        ) : (
                            <Text variant="text-sm/leading-normal/normal">{attachment.name}</Text>
                        )}
                    </div>
                    {canModify && (
                        <Button
                            variant="tertiary"
                            size="icon"
                            disabled={loading}
                            onClick={() => onDelete(attachment.id)}
                        >
                            <Icon name="TrashBinSimple" size="medium" className="fill-danger-500" />
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );
}

export function NotesTab({ section, prop, onUpdated }: NotesTabProps) {
    const notes = section?.notes || [];
    const canEdit = prop?.role ? prop.role !== 'view' : !prop?.is_shared;
    const isOwner = prop?.role ? prop.role === 'owner' : !prop?.is_shared;
    const currentUserId = (Window as any)?.user_data?.loggedInUserID;

    const [newNote, setNewNote] = useState('');
    const [newNoteFiles, setNewNoteFiles] = useState<FileItem[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [editingFiles, setEditingFiles] = useState<FileItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);

    const applyResponse = (resp: { ok: boolean; data?: any; error?: string; message?: string }) => {
        if (resp.ok && resp.data) {
            onUpdated(resp.data.ref, resp.data.new_data);
            setError(null);
            return true;
        }
        setError(resp.error || resp.message || 'Något gick fel');
        return false;
    };

    const handleAdd = async () => {
        const text = newNote.trim();
        const files = filesFromItems(newNoteFiles);
        if ((!text && files.length === 0) || loading) {
            return;
        }

        const validationError = validateUploadFiles(files);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        const resp = await addSectionNote(section.nr, text, files);
        setLoading(false);
        if (applyResponse(resp)) {
            setNewNote('');
            setNewNoteFiles([]);
            setShowCreateForm(false);
        }
    };

    const startEdit = (note: SectionNote) => {
        setEditingId(note.id as number);
        setEditingText(note.text);
        setEditingFiles([]);
        setError(null);
    };

    const handleSaveEdit = async (noteId: number) => {
        const text = editingText.trim();
        const note = notes.find((n) => n.id === noteId);
        const existingAttachments = note?.attachments?.length ?? 0;
        const newFiles = filesFromItems(editingFiles);

        if (!text && existingAttachments === 0 && newFiles.length === 0) {
            setError('Anteckningen måste ha text eller minst en bilaga');
            return;
        }

        const validationError = validateUploadFiles(newFiles);
        if (validationError) {
            setError(validationError);
            return;
        }

        if (loading) {
            return;
        }

        setLoading(true);
        const resp = await updateSectionNote(noteId, text);
        if (!resp.ok) {
            setLoading(false);
            applyResponse(resp);
            return;
        }

        if (newFiles.length > 0) {
            const attachResp = await addSectionNoteAttachment(noteId, newFiles);
            setLoading(false);
            if (!applyResponse(attachResp)) {
                return;
            }
        } else {
            setLoading(false);
            applyResponse(resp);
        }

        setEditingId(null);
        setEditingText('');
        setEditingFiles([]);
    };

    const handleDelete = async (noteId: number) => {
        if (loading) {
            return;
        }
        setLoading(true);
        const resp = await deleteSectionNote(noteId);
        setLoading(false);
        applyResponse(resp);
    };

    const handleDeleteAttachment = async (attachmentId: number) => {
        if (loading) {
            return;
        }
        setLoading(true);
        const resp = await deleteSectionNoteAttachment(attachmentId);
        setLoading(false);
        applyResponse(resp);
    };

    const canModifyNote = (note: SectionNote) =>
        canEdit && note.id !== null && (isOwner || note.author_id === currentUserId);

    const canSubmitNew = Boolean(newNote.trim() || filesFromItems(newNoteFiles).length > 0);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
                <Text variant="text-sm/leading-normal/medium" className="px-1">
                    Anteckningar
                </Text>

                {error && !canEdit && (
                    <Text variant="text-sm/leading-normal/normal" className="mt-2 px-1 text-danger-500">
                        {error}
                    </Text>
                )}

                {notes.length === 0 && (
                    <Text variant="text-sm/leading-normal/normal" className="mt-2 px-1 text-[#737373]">
                        Inga anteckningar ännu.
                    </Text>
                )}

                <div className="mt-2 flex flex-col gap-2">
                    {notes.map((note, index) => (
                        <div
                            key={note.id ?? `legacy-${index}`}
                            className="flex flex-col gap-2 rounded-lg border border-solid border-[#E6E6E6] p-3"
                        >
                            {editingId === note.id ? (
                                <>
                                    <Textarea
                                        name={`note-${note.id}`}
                                        className="min-h-24"
                                        value={editingText}
                                        onChange={setEditingText}
                                        placeholder="Anteckning (valfritt om du har bilagor)"
                                    />
                                    <NoteAttachments
                                        attachments={note.attachments || []}
                                        canModify={canModifyNote(note)}
                                        loading={loading}
                                        onDelete={handleDeleteAttachment}
                                    />
                                    <TwFileUpload
                                        name={`edit-note-files-${note.id}`}
                                        accept={SECTION_NOTE_ACCEPT}
                                        label=""
                                        multiple
                                        buttonText="Lägg till bilagor"
                                        value={editingFiles}
                                        onChange={(value) => setEditingFiles(value)}
                                        idleText="Bilder, PDF, Word, Excel m.m. (max 100 MB per fil)"
                                        errorText="Kunde inte ladda upp filen"
                                        successText="Filer tillagda"
                                    />
                                    <div className="flex flex-row justify-end gap-2">
                                        <Button variant="tertiary" size="xsmall" onClick={() => setEditingId(null)}>
                                            Avbryt
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="xsmall"
                                            disabled={loading}
                                            onClick={() => handleSaveEdit(note.id as number)}
                                        >
                                            Spara
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {note.text ? (
                                        <Text variant="text-sm/leading-normal/normal" className="whitespace-pre-line">
                                            {note.text}
                                        </Text>
                                    ) : null}
                                    <NoteAttachments
                                        attachments={note.attachments || []}
                                        canModify={canModifyNote(note)}
                                        loading={loading}
                                        onDelete={handleDeleteAttachment}
                                    />
                                    <div className="flex flex-row items-center justify-between">
                                        <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                            {[note.author_name, note.created_at].filter(Boolean).join(' · ') || 'Okänd'}
                                        </Text>
                                        {canModifyNote(note) && (
                                            <div className="flex flex-row gap-1">
                                                <Button
                                                    variant="tertiary"
                                                    size="icon"
                                                    onClick={() => startEdit(note)}
                                                >
                                                    <Icon name="EditPenSimple" size="medium" />
                                                </Button>
                                                <Button
                                                    variant="tertiary"
                                                    size="icon"
                                                    disabled={loading}
                                                    onClick={() => handleDelete(note.id as number)}
                                                >
                                                    <Icon name="TrashBinSimple" size="medium" className="fill-danger-500" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {error && canEdit && editingId !== null && (
                    <Text variant="text-sm/leading-normal/normal" className="mt-2 px-1 text-danger-500">
                        {error}
                    </Text>
                )}
            </div>

            {canEdit && (
                <div className="shrink-0 border-t border-solid border-[#E6E6E6] bg-white">
                    {!showCreateForm ? (
                        <div className="px-4 py-3">
                            <Button
                                variant="secondary"
                                size="small"
                                fullWidth
                                onClick={() => {
                                    setShowCreateForm(true);
                                    setError(null);
                                }}
                            >
                                <Icon name="EditPenSimple" size="medium" className="me-2" />
                                Lägg till anteckning
                            </Button>
                        </div>
                    ) : (
                        <div className="px-4 py-3">
                            <div className="mb-2 flex flex-row items-center justify-between px-1">
                                <Text variant="text-sm/leading-normal/medium">Ny anteckning</Text>
                                <Button
                                    variant="tertiary"
                                    size="xsmall"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setError(null);
                                    }}
                                >
                                    Avbryt
                                </Button>
                            </div>

                            {error && editingId === null && (
                                <Text variant="text-sm/leading-normal/normal" className="mb-2 px-1 text-danger-500">
                                    {error}
                                </Text>
                            )}

                            <div className="flex flex-col gap-2">
                                <Textarea
                                    name="new-note"
                                    className="min-h-20"
                                    value={newNote}
                                    onChange={setNewNote}
                                    placeholder="Skriv en anteckning (valfritt om du bifogar filer)"
                                />
                                <TwFileUpload
                                    name="new-note-files"
                                    accept={SECTION_NOTE_ACCEPT}
                                    label=""
                                    multiple
                                    buttonText="Välj bilagor"
                                    value={newNoteFiles}
                                    onChange={(value) => setNewNoteFiles(value)}
                                    idleText="Bilder, PDF, Word, Excel m.m. (max 100 MB per fil)"
                                    errorText="Kunde inte ladda upp filen"
                                    successText="Filer tillagda"
                                />
                                <Button
                                    variant="secondary"
                                    size="small"
                                    fullWidth
                                    disabled={loading || !canSubmitNew}
                                    onClick={handleAdd}
                                >
                                    Lägg till anteckning
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
